/**
 * RehearsalStage — the score above, the take log across the middle, the
 * cue rail below.
 *
 * That sentence is `DESIGN_SPEC.md` §9's own, from the row that forbids
 * the 3-pane system/chat/parameters layout: *"Rehearsal is ensemble-first and
 * time-first: the score above, the take log running left-to-right in the
 * middle, the cue rail of dials along the bottom, bond curves under each
 * voice."* `docs/console-architecture.md` §4.5 has drawn the same
 * wireframe since it was written.
 *
 * This component was a three-column grid containing a vertical log until
 * animachord#55, which contradicted two of §9's explicit negatives at
 * once. The rebuild is the decision Sean took on that issue. What
 * changed, and why each piece is where it is:
 *
 * - **The take log is a band, not a column.** Its axis is time and §9
 *   makes time the dominant axis of the screen, which a column cannot
 *   be. It also ends a class of bug rather than one instance: the log as
 *   a column kept losing width to its neighbours -- most recently to
 *   roughly 330px at 1500px wide, which shredded Margo's opening line --
 *   and a full-width band has no column to lose.
 * - **The note field hangs off the right edge of the log**, per §4.3,
 *   passed into `TakeLogStream`'s cursor slot rather than sitting in the
 *   bar below. It is the head of the timeline, so it belongs at the head
 *   of the timeline; the bar keeps `send cue`, which acts on the
 *   performance rather than on the take.
 * - **The cue rail and the state panel share the bottom band.** Neither
 *   flanks the log, so this is not the silhouette §9 rejects: the log
 *   spans the full stage above them. The panel keeps the 22.5rem measure
 *   it was designed and reviewed at (`docs/evidence/panel-v1/`) and drops
 *   under the cue rail below 1280px rather than being squeezed.
 *
 * Presentational: no data fetching, no socket plumbing. The Rehearsal
 * page owns the state and passes it down — keeps this component testable
 * in isolation and lets the page swap socket transports later.
 */

import { useCallback, useState, type ReactElement } from "react";

import CastRail, { type CastMember } from "@/components/rehearsal/CastRail";
import { CueRail, type Cue } from "@/components/primitives/CueRail";
import { ScoreEditor, type ScoreDraft } from "@/components/primitives/ScoreEditor";
import { TakeLogStream, type LogLine } from "@/components/primitives/TakeLogStream";
import type { StageSocketState } from "@/api";

import { rehearsalStageCopy } from "./RehearsalStage.copy";

export interface RehearsalStageProps {
  readonly score: ScoreDraft;
  readonly cues: readonly Cue[];
  readonly logLines: readonly LogLine[];
  readonly connectionState: StageSocketState;
  readonly onSendCue: () => void;
  /**
   * Send a typed line as `client.human-note`.
   *
   * Not optional, and not a fallback that appears only when voice
   * fails. `docs/plan-research/content-voice-ux.md` is explicit that a
   * mic-blocked browser must still be able to try a rehearsal, and
   * voice is off entirely unless the deployment carries both a
   * Deepgram and an ElevenLabs key — which the local stack usually
   * does not. Without this the visitor has no way to say anything.
   */
  readonly onSendNote: (text: string) => void;

  /** Who is in the room. From `server.hello.voices` and `server.cast`. */
  readonly cast: readonly CastMember[];
  /** Whose lamp is lit: the voice a typed line is addressed to. */
  readonly addressed: string;
  readonly onAddress: (voiceId: string) => void;
  readonly onCreateCharacter: (sentence: string) => void;
  readonly creatingCharacter?: boolean;
  readonly createCharacterError?: string | null;
  readonly castFull?: boolean;

  /**
   * The state panel, in the bottom band beside the cue rail.
   *
   * Passed in rather than built here for the same reason everything else
   * on this component is: the page owns the state and this stays
   * presentational. Optional so the stage renders unchanged where there
   * is no panel to show, which is every existing test.
   */
  readonly panel?: ReactElement | null;
}

const STATE_COLOR: Record<StageSocketState, string> = {
  connecting: "var(--brass-tarnish)",
  reconnecting: "var(--brass-tarnish)",
  open: "var(--brass-strike)",
  closing: "var(--brass-tarnish)",
  closed: "var(--dynamic-forte)",
};

export function RehearsalStage({
  score,
  cues,
  logLines,
  connectionState,
  onSendCue,
  onSendNote,
  cast,
  addressed,
  onAddress,
  onCreateCharacter,
  creatingCharacter = false,
  createCharacterError = null,
  castFull = false,
  panel = null,
}: RehearsalStageProps): ReactElement {
  const sendDisabled = connectionState !== "open";
  const [noteText, setNoteText] = useState<string>("");
  const noteDisabled = sendDisabled || noteText.trim().length === 0;

  const submitNote = useCallback((): void => {
    const text = noteText.trim();
    if (text.length === 0) return;
    onSendNote(text);
    setNoteText("");
  }, [noteText, onSendNote]);

  /*
    The head of the timeline. Column rather than row, and narrow, because
    it stands at the right edge of a band whose height is set by the note
    cards beside it -- a wide row here would take width from the take
    itself, which is the thing the reader came for.
  */
  const noteForm = (
    <form
      className="take-log__composer"
      // Width in `index.css`, not here: it has to change on a phone, and
      // an inline width would win over the media query that changes it.
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.4rem",
        margin: 0,
      }}
      onSubmit={(event): void => {
        event.preventDefault();
        submitNote();
      }}
    >
      <input
        type="text"
        data-testid="rehearsal-note-input"
        aria-label={rehearsalStageCopy.noteInputLabel}
        placeholder={rehearsalStageCopy.noteInputPlaceholder}
        value={noteText}
        disabled={sendDisabled}
        onChange={(event): void => setNoteText(event.target.value)}
        className="font-plex-mono"
        style={{
          minWidth: 0,
          background: "var(--stage-carbon)",
          color: "var(--paper-cream)",
          border: "1px solid var(--measure-line)",
          borderRadius: "var(--radius)",
          padding: "0.4rem 0.6rem",
          fontSize: "0.875rem",
        }}
      />
      <button
        type="submit"
        data-testid="rehearsal-send-note"
        disabled={noteDisabled}
        className="font-plex-mono"
        style={{
          background: noteDisabled ? "var(--rehearsal-taupe)" : "var(--brass-strike)",
          color: noteDisabled ? "var(--paper-cream-dim)" : "var(--score-ink)",
          border: "1px solid var(--measure-line)",
          borderRadius: "var(--radius)",
          padding: "0.4rem 0.9rem",
          fontSize: "0.781rem",
          cursor: noteDisabled ? "not-allowed" : "pointer",
        }}
      >
        {rehearsalStageCopy.sendNoteLabel}
      </button>
    </form>
  );

  return (
    <section data-testid="rehearsal-stage" className="rehearsal-stage">
      {/* ── the score, above ─────────────────────────────────────────── */}
      {/*
        A band, and it opens.

        Found by rendering the rebuilt stage at 1440x900 rather than by
        reading it: the read-only `ScoreEditor` is about 640px tall, most
        of it a paper-cream voice-line textarea, and above a full-width
        take log that put the log entirely below the fold. The score is
        meant to be above the take log; it is not meant to be instead of
        it, and §9's sentence puts the log in the middle of the screen.

        `docs/console-architecture.md` §4.5 draws this as **ScoreInline**,
        a compact strip of one line per voice, so the summary is the
        documented shape rather than an invention. `details` rather than a
        new component: the editor is real and a developer scoring an
        ensemble wants it, so it opens on demand and the disclosure state
        is the browser's, which means it survives with JavaScript doing
        nothing and reads correctly to a screen reader.
      */}
      <details data-testid="rehearsal-score-band" aria-label={rehearsalStageCopy.scoreBandLabel}>
        <summary
          data-testid="rehearsal-score-summary"
          className="font-plex-mono"
          style={{
            cursor: "pointer",
            listStyle: "none",
            display: "flex",
            alignItems: "baseline",
            gap: "0.75rem",
            padding: "0.4rem 0.6rem",
            background: "var(--stage-riser)",
            border: "1px solid var(--measure-line)",
            borderRadius: "var(--radius)",
            fontSize: "0.781rem",
            color: "var(--paper-cream)",
          }}
        >
          <span
            style={{
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              fontSize: "0.656rem",
              color: "var(--paper-cream-faint)",
            }}
          >
            {rehearsalStageCopy.scoreBandLabel}
          </span>
          <span>{score.name}</span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "var(--paper-cream-dim)",
            }}
          >
            {score.description}
          </span>
          <span style={{ color: "var(--brass-cue)", fontSize: "0.656rem" }}>
            {rehearsalStageCopy.scoreDisclosureLabel}
          </span>
        </summary>
        <div style={{ paddingTop: "0.75rem" }}>
          <ScoreEditor score={score} onChange={(): void => undefined} readOnly />
        </div>
      </details>

      {/* ── the take log, across the middle ──────────────────────────── */}
      <div
        data-testid="rehearsal-log-band"
        aria-label={rehearsalStageCopy.logBandLabel}
        style={{ display: "flex", minWidth: 0 }}
      >
        <TakeLogStream lines={logLines} input={noteForm} />
      </div>

      {/* ── the cue rail along the bottom, and the panel beside it ───── */}
      <div className="rehearsal-stage__band" data-has-panel={panel ? "true" : "false"}>
        <div
          data-testid="rehearsal-cue-band"
          aria-label={rehearsalStageCopy.cueBandLabel}
          style={{ display: "flex", flexDirection: "column", gap: "0.75rem", minWidth: 0 }}
        >
          <CueRail cues={cues} />
          <div
            style={{
              display: "flex",
              // Top rather than centre: opening the character composer makes
              // the rail taller than its siblings, and centring would drift
              // the controls downward as it opens. The bar's controls stay
              // on one line and the composer grows below them.
              alignItems: "flex-start",
              justifyContent: "space-between",
              /*
                Wrap, or the bar cannot fit a phone. Status, cast rail and
                the cue button on one unwrappable row measured wider than a
                393px viewport, which is the one overflow a visitor feels
                rather than merely sees.
              */
              flexWrap: "wrap",
              gap: "0.75rem",
              padding: "0.5rem 0.75rem",
              background: "var(--stage-pit)",
              border: "1px solid var(--measure-line)",
              borderRadius: "var(--radius)",
            }}
          >
            <span
              data-testid="rehearsal-connection-status"
              data-state={connectionState}
              className="font-plex-mono"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                color: "var(--paper-cream-dim)",
                fontSize: "0.781rem",
              }}
            >
              <span
                aria-hidden
                style={{
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "50%",
                  background: STATE_COLOR[connectionState],
                }}
              />
              {rehearsalStageCopy.status[connectionState]}
            </span>
            <CastRail
              cast={cast}
              addressed={addressed}
              onAddress={onAddress}
              onCreate={onCreateCharacter}
              creating={creatingCharacter}
              createError={createCharacterError}
              castFull={castFull}
              disabled={sendDisabled}
            />
            <button
              type="button"
              data-testid="rehearsal-send-cue"
              disabled={sendDisabled}
              onClick={onSendCue}
              className="font-plex-mono"
              style={{
                background: sendDisabled ? "var(--rehearsal-taupe)" : "var(--brass-cue)",
                color: sendDisabled ? "var(--paper-cream-dim)" : "var(--score-ink)",
                border: "1px solid var(--measure-line)",
                borderRadius: "var(--radius)",
                padding: "0.4rem 0.9rem",
                fontSize: "0.781rem",
                cursor: sendDisabled ? "not-allowed" : "pointer",
              }}
            >
              {rehearsalStageCopy.sendCueLabel}
            </button>
          </div>
        </div>
        {panel}
      </div>
    </section>
  );
}

export default RehearsalStage;
